import { useCallback, useEffect, useState } from "react";

// Theme preference (Brändiehdotus v2): tokens flip at the :root level.
//   • "system" — follow the OS (prefers-color-scheme), no data-theme attribute
//   • "light" / "dark" — an explicit override that wins over the OS either way
// The choice persists in localStorage and is applied to <html data-theme>. The
// initial attribute is also set by an inline script in index.html to avoid a
// flash of the wrong theme before React mounts.
export type ThemePref = "system" | "light" | "dark";

export const THEME_STORAGE_KEY = "verso-theme";

export function readStoredTheme(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === "light" || v === "dark") return v;
  } catch {
    /* private mode / storage disabled — fall through to system */
  }
  return "system";
}

export function applyTheme(pref: ThemePref): void {
  const root = document.documentElement;
  if (pref === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", pref);
}

export function persistTheme(pref: ThemePref): void {
  try {
    if (pref === "system") localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    /* ignore persistence failures */
  }
}

// Hook: current preference + a setter that applies and persists in one call.
export function useTheme(): [ThemePref, (pref: ThemePref) => void] {
  const [pref, setPref] = useState<ThemePref>(() => readStoredTheme());

  // Reflect the stored value on mount (in case it changed between the inline
  // script running and React hydrating).
  useEffect(() => {
    applyTheme(pref);
  }, [pref]);

  const set = useCallback((next: ThemePref) => {
    persistTheme(next);
    applyTheme(next);
    setPref(next);
  }, []);

  return [pref, set];
}
